import { createResource, createMemo, onCleanup, Show } from 'solid-js';
import { customShadowlessElement, type CorrectComponentType } from '@brytdesigns/web-component-core/utils';
import { correctElementType } from '@brytdesigns/web-component-core';
import { useCartPath as _useCartPath, useCartValue as _useCartValue } from '@brytdesigns/shopify-ajax-cart-components';

// The library types these hooks for absolute cart paths only, but at runtime
// they accept paths relative to the nearest provided cart-path context
// (e.g. the <cart-data-array-item> wrapping each line item).
const useCartPath = _useCartPath as unknown as (props: { element: HTMLElement; path?: () => string }) => () => string;

const useCartValue = _useCartValue as unknown as <T>(props: {
  element: HTMLElement;
  path: () => string;
}) => () => T | undefined;

type BulkPriceTier = { threshold: number; price: number };

type BulkVariant = {
  id: number;
  title: string;
  sku: string;
  price: number;
  bulk_prices: BulkPriceTier[];
};

type BulkPricingResponse = {
  product_id: number;
  handle: string;
  currency: string;
  variants: BulkVariant[];
};

type Props = {
  dataMessageTemplate: string;
};

const responseCache = new Map<string, Promise<BulkPricingResponse>>();

const fetchBulkPricing = (url: string, signal: AbortSignal): Promise<BulkPricingResponse> => {
  const cached = responseCache.get(url);
  if (cached) return cached;

  const promise = (async () => {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`bulk-pricing fetch failed: ${response.status}`);
    const text = await response.text();
    return JSON.parse(text) as BulkPricingResponse;
  })();

  responseCache.set(url, promise);
  promise.catch(() => responseCache.delete(url));
  return promise;
};

const formatMoney = (cents: number): string => {
  if (window.Shopify?.formatMoney) {
    return window.Shopify.formatMoney(cents, window.Shopify.money_format);
  }
  return `$${(cents / 100).toFixed(2)}`;
};

const Component: CorrectComponentType<Props> = (props, { element }) => {
  // ---------------------------------------------------------------------------
  // Null-blocker pattern.
  //
  // Solid web components' setup function only runs once on mount — there's no
  // virtual DOM re-run on state change. So we have one shot to either resolve
  // every dependency or bail completely. If we throw here, the throw aborts
  // the render frame the parent (cart-data-array, cart-data-conditions, etc.)
  // is in the middle of, which can leave drawer-context's open/close cleanup
  // dangling and surface as the body-scroll lock failing to release.
  //
  // Layered defenses:
  //   1) `closest('cart-context')` short-circuits when the element is mounted
  //      outside the cart context entirely (e.g. design-mode preview or a
  //      misconfigured block placement).
  //   2) The hook calls are wrapped in try/catch. `useCartValue` throws when
  //      it can't find the cart context provider on its DOM walk; if that
  //      somehow fires after the closest check passes, we still don't take
  //      down the parent render frame.
  //   3) The reactive memos all guard on `handle()` / `variantId()` /
  //      `quantity()` being defined, so even if the hooks resolve to no-op
  //      accessors we don't try to fetch or render anything.
  //   4) The render is wrapped in a single Show so the element is genuinely
  //      empty when there's nothing to say (no tiers, no template, no message).
  // ---------------------------------------------------------------------------

  if (!element.closest('cart-context')) return null;

  let handle: () => string | undefined = () => undefined;
  let variantId: () => number | undefined = () => undefined;
  let quantity: () => number | undefined = () => undefined;

  try {
    const handlePath = useCartPath({ element, path: () => 'handle' });
    const variantIdPath = useCartPath({ element, path: () => 'variant_id' });
    const quantityPath = useCartPath({ element, path: () => 'quantity' });

    handle = useCartValue<string>({ element, path: handlePath });
    variantId = useCartValue<number>({ element, path: variantIdPath });
    quantity = useCartValue<number>({ element, path: quantityPath });
  } catch {
    // The hooks would have logged via console.error already; swallow the throw
    // so the parent render frame survives. Accessors stay as no-op fallbacks
    // and every downstream memo bails on the undefined values.
    return null;
  }

  const url = createMemo(() => {
    const h = handle();
    return h ? `/products/${h}?view=bulk-pricing` : null;
  });

  const [data] = createResource(url, (u) => {
    const controller = new AbortController();
    onCleanup(() => controller.abort());
    return fetchBulkPricing(u, controller.signal);
  });

  const tiers = createMemo<BulkPriceTier[]>(() => {
    const d = data();
    const id = variantId();
    if (!d || !id) return [];
    const variant = d.variants.find((v) => v.id === id);
    if (!variant || variant.bulk_prices.length === 0) return [];
    return [...variant.bulk_prices].sort((a, b) => a.threshold - b.threshold);
  });

  const message = createMemo<string | null>(() => {
    const t = tiers();
    const q = quantity() ?? 0;
    const template = props.dataMessageTemplate;
    if (!template || t.length === 0) return null;
    const next = t.find((tier) => q < tier.threshold);
    if (!next) return null;
    return template.replace('{count}', String(next.threshold - q)).replace('{price}', formatMoney(next.price));
  });

  return (
    <Show when={message()}>
      <span>{message()}</span>
    </Show>
  );
};

customShadowlessElement('bulk-pricing', { dataMessageTemplate: '' }, correctElementType(Component));
